import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Select, Input, Button, Table, message, DatePicker } from 'antd';
import moment from 'moment';
import { EditOutlined } from '@ant-design/icons';
import { SelectEmployee } from '../SelectInput';
import { getRequest, postRequest } from '../../utils/APICall';

const { Option } = Select;

const AddPayStructureForm = ({ visible, onCancel, onOk }) => {
  const [entries, setEntries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [salaryHead, setSalaryHead] = useState(null);
  const [deductionForSummary, setDeductionForSummary] = useState('');
  const [deductionAmount, setDeductionAmount] = useState('');
  const { allEmployee } = useSelector((state) => state.employees);
  const { users } = useSelector((state) => state.user || {})
  function removeDuplicatesByID(array) {
    const seen = new Set();
    return array.filter((item) => {
      if (seen.has(item.ID)) {
        return false;
      }
      seen.add(item.ID);
      return true;
    });
  }
  const filterData = removeDuplicatesByID(allEmployee)
  const [formData, setFormData] = useState({
    empName: '',
    year: '',
    type: '',
    earning: '',
  });

  const resetForm = () => {
    setFormData({ empName: '', year: '', type: '', earning: '' });
    setEntries([]);
    setEmployeeId(null);
    setEditingIndex(null);
    setDeductionForSummary('');
    setDeductionAmount('');
  };

  const handleAddOrUpdate = () => {
    const { type, earning } = formData;
    if (!type || !earning) {
      message.error('Please select an income head and enter an amount.');
      return;
    }
    const newEntry = { type, earning: earning || '0' };
    if (editingIndex !== null) {
      const updatedEntries = [...entries];
      updatedEntries[editingIndex] = newEntry;
      setEntries(updatedEntries);
      setEditingIndex(null);
    } else {
      setEntries([...entries, newEntry]);
    }
    setFormData({ ...formData, type: '', earning: '' });
    setDeductionForSummary('');
    setDeductionAmount('');
  };

  const handleAddDeductionFromSummary = () => {
    if (!deductionForSummary || !deductionAmount) {
      message.error('Please select a deduction head and enter an amount.');
      return;
    }
    const newEntry = { type: deductionForSummary, earning: deductionAmount || '0' };

    if (editingIndex !== null) {
      // Update existing deduction
      const updatedEntries = [...entries];
      updatedEntries[editingIndex] = newEntry;
      setEntries(updatedEntries);
      setEditingIndex(null);
    } else {
      // Add new deduction
      if (entries.some((entry) => entry.type === deductionForSummary)) {
        message.error('This deduction head is already added.');
        return;
      }
      setEntries([...entries, newEntry]);
    }

    setDeductionForSummary('');
    setDeductionAmount('');
  };

  const getSalaryHead = async () => {
    const res = await getRequest('getSalaryHead');
    setSalaryHead(res?.data);
  };

  useEffect(() => {
    getSalaryHead();
  }, []);

  const handleSubmit = async () => {
    const { year } = formData;
    if (!employeeId || !year || entries.length === 0) {
      message.error('Please fill all required fields.');
      return;
    }
    
    const payload = {
      salarystruct_id: null,
      vempName: String(employeeId),
      issuedDate: moment(year).toDate(),
      entries: entries.map((entry) => ({ vtype: entry.type, vearning: entry.earning })),
      vcreatedBy: `${users?.FIRSTNAME} ${users?.LASTNAME} | ${moment(Date.now()).format('DD-MM-YYYY HH:mm')}`,
    };
    const hasDuplicate = entries.some(
      (entry, index) => entries.findIndex((e) => e.type === entry.type) !== index
    );
    if (hasDuplicate) {
      message.error('Duplicate salary head types are not allowed.');
      return;
    }
    try {
      // console.log("payload", payload)
      const res = await postRequest('addSalaryStructure', payload);
      // console.log(res)
      if (res?.success) {
        message.success(res.message);
        resetForm();
        onOk();
        window.location.reload();
      } else {
        throw new Error(res.error || 'Something went wrong');
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const getAvailablePositiveHeads = () => {
    if (!salaryHead) return [];
    return salaryHead.filter(
      (item) =>
        !item?.HEAD?.toLowerCase()?.includes('deduction') &&
        !item?.HEAD?.toLowerCase()?.includes('income tax') &&
        !item?.HEAD?.toLowerCase()?.includes('loan') &&
        !entries.some((entry) => entry.type === item.ID)
    );
  };

  const getAvailableDeductionHeads = () => {
    if (!salaryHead) return [];
    return salaryHead.filter(
      (item) =>
        (item?.HEAD?.toLowerCase()?.includes('deduction') ||
          item?.HEAD?.toLowerCase()?.includes('income tax') ||
          item?.HEAD?.toLowerCase()?.includes('loan')) &&
        !entries.some((entry) => entry.type === item.ID)
    );
  };

  const getTableDataSource = () => {
    const incomeEntries = entries.filter(
      (entry) =>
        !salaryHead?.find((item) => item.ID === entry.type)?.HEAD?.toLowerCase()?.includes('deduction') &&
        !salaryHead?.find((item) => item.ID === entry.type)?.HEAD?.toLowerCase()?.includes('income tax') &&
        !salaryHead?.find((item) => item.ID === entry.type)?.HEAD?.toLowerCase()?.includes('loan')
    );
    const deductionEntries = entries.filter((entry) =>
      salaryHead?.find((item) => item.ID === entry.type)?.HEAD?.toLowerCase()?.includes('deduction') ||
      salaryHead?.find((item) => item.ID === entry.type)?.HEAD?.toLowerCase()?.includes('income tax') ||
      salaryHead?.find((item) => item.ID === entry.type)?.HEAD?.toLowerCase()?.includes('loan')
    );

    let totalIncome = 0;
    let totalDeduction = 0;

    entries.forEach((entry) => {
      const headObj = salaryHead?.find((item) => item.ID === entry.type);
      const isDeduction = headObj?.HEAD?.toLowerCase()?.includes('deduction') ||
        headObj?.HEAD?.toLowerCase()?.includes('income tax') ||
        headObj?.HEAD?.toLowerCase()?.includes('loan');
      const amount = parseFloat(entry.earning || '0');
      if (isDeduction) {
        totalDeduction += amount;
      } else {
        totalIncome += amount;
      }
    });

    const netTotal = totalIncome - totalDeduction;

    return [
      ...incomeEntries.map((entry, index) => ({
        ...entry,
        key: `income-${entry.type}-${index}`,
        rowType: 'income',
      })),
      {
        key: 'total-income',
        rowType: 'total-income',
        type: 'Total Income',
        income: totalIncome,
      },
      {
        key: 'add-deduction',
        rowType: 'add-deduction',
        type: 'Deduction',
      },
      ...deductionEntries.map((entry, index) => ({
        ...entry,
        key: `deduction-${entry.type}-${index}`,
        rowType: 'deduction',
      })),
      {
        key: 'total-deduction',
        rowType: 'total-deduction',
        type: 'Total Deduction:',
        deduction: totalDeduction,
      },
      {
        key: 'net-total',
        rowType: 'net-total',
        type: 'Net Total',
        income: netTotal,
      },
    ];
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      render: (text, record) => {
        if (['total-income', 'net-total', 'total-deduction'].includes(record.rowType)) {
          return <span className="font-bold text-sm">{text}</span>;
        }
        if (record.rowType === 'add-deduction') {
          return <span className="font-bold text-md">Deduction</span>;
        }
        const headObj = salaryHead?.find((item) => item.ID === text);
        return <span className="text-sm">{headObj ? headObj.HEAD : text}</span>;
      },
    },
    {
      title: 'Income',
      dataIndex: 'earning',
      render: (earning, record) => {
        if (record.rowType === 'total-income') {
          return <span className="font-bold text-sm">{record.income}</span>;
        }
        if (record.rowType === 'income') {
          return <span className="text-sm !p-0 !m-0">{earning}</span>;
        }
        if (record.rowType === 'add-deduction') {
          return (
            <Select
              showSearch
              placeholder="Select Deduction"
              value={deductionForSummary}
              onChange={(val) => setDeductionForSummary(val)}
              className="w-[150px] !h-7"
            >
              {getAvailableDeductionHeads()?.map((item) => (
                <Option key={item.ID} value={item.ID} className="text-xs">
                  {item.HEAD}
                </Option>
              ))}
            </Select>
          );
        }
        return '';
      },
    },
    {
      title: 'Deduction',
      dataIndex: 'earning',
      render: (earning, record) => {
        if (record.rowType === 'total-deduction') {
          return <span className="font-bold text-sm">{record.deduction}</span>;
        }
        if (record.rowType === 'deduction') {
          return <span className="text-sm">{earning}</span>;
        }
        if (record.rowType === 'add-deduction') {
          return (
            <Input
              placeholder="Amount"
              value={deductionAmount}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setDeductionAmount(value);
              }}
              className="w-[150px] !h-7 !text-xs"
            />
          );
        }
        return '';
      },
    },
    {
      title: 'Action',
      render: (_, record) => {
        if (record.rowType === 'net-total') {
          return <span className="font-bold text-sm">{record.income}</span>;
        }
        if (['income', 'deduction'].includes(record.rowType)) {
          const originalIndex = entries.findIndex(
            (entry) => entry.type === record.type && entry.earning === record.earning
          );
          return (
            <Button
              type="text"
              icon={<EditOutlined className="text-sm" />}
              className="!p-0 !h-6"
              onClick={() => {
                const entry = entries[originalIndex];
                const isDeduction =
                  salaryHead
                    ?.find((item) => item.ID === entry.type)
                    ?.HEAD?.toLowerCase()
                    ?.includes('deduction') ||
                  salaryHead
                    ?.find((item) => item.ID === entry.type)
                    ?.HEAD?.toLowerCase()
                    ?.includes('income tax') ||
                  salaryHead
                    ?.find((item) => item.ID === entry.type)
                    ?.HEAD?.toLowerCase()
                    ?.includes('loan');
                if (isDeduction) {
                  setDeductionForSummary(entry.type);
                  setDeductionAmount(entry.earning);
                  setFormData({ ...formData, type: '', earning: '' });
                } else {
                  setFormData({ ...formData, type: entry.type, earning: entry.earning });
                  setDeductionForSummary('');
                  setDeductionAmount('');
                }
                setEditingIndex(originalIndex);
              }}
            />
          );
        }
        if (record.rowType === 'add-deduction') {
          return (
            <Button
              type="primary"
              size="small"
              className="bg-green-500 text-xs !h-7 !px-12"
              onClick={handleAddDeductionFromSummary}
            >
              {editingIndex !== null && deductionForSummary ? 'Update' : 'Add'}
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <Modal
      title={<span className="flex justify-center text-xl font-bold">Add Salary Structure</span>}
      open={visible}
      onCancel={() => {
        resetForm();
        onCancel();
      }}
      footer={null}
      width="100%"
      className="max-w-3xl mx-auto rounded-lg shadow-lg"
    >
      <div className="flex flex-col gap-2 mb-2 p-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="w-full sm:w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name <span className="text-red-500">*</span>
            </label>
            <SelectEmployee
              employeeList={filterData}
              onChange={(value) => setEmployeeId(value)}
              selectedEmployee={employeeId}
              customStyle="w-full"
              allLabel={{}}
            />
          </div>
          <div className="w-full sm:w-1/2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            <DatePicker
              picker="date"
              className="w-full h-10 border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
              placeholder="Select Date"
              format="DD MMMM YYYY"
              onChange={(date, dateString) =>
                setFormData({ ...formData, year: dateString })
              }
            />
          </div>
        </div>
      </div>
      <div className="p-2 bg-white rounded-lg shadow-inner mb-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Income Type</label>
            <Select
              showSearch
              placeholder="Select Income Head"
              value={formData.type}
              onChange={(val) => setFormData({ ...formData, type: val, earning: formData.earning })}
              className="w-full border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
              disabled={editingIndex !== null && deductionForSummary}
            >
              {getAvailablePositiveHeads()?.map((item) => (
                <Option key={item.ID} value={item.ID}>
                  {item.HEAD}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
            <Input
              placeholder="Enter amount"
              value={formData.earning}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setFormData({ ...formData, earning: value });
              }}
              className="w-full border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="primary"
              className="w-full bg-green-500 text-white hover:bg-green-600 rounded-md"
              onClick={handleAddOrUpdate}
            >
              {editingIndex !== null && !deductionForSummary ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table
          columns={columns}
          dataSource={getTableDataSource()}
          pagination={false}
          rowKey="key"
          className=" !p-0 !m-0 !h-96 "
          rowClassName={(record) =>
            ['total-income', 'add-deduction', 'total-deduction', 'net-top'].includes(record.rowType)
              ? 'py-0 h-6 !p-0 !m-0 ' // Further reduced padding and height for specific rows
              : 'py-0.5 !p-0 !m-0  '
          }
        />
      </div>
      <div className="flex flex-col sm:flex-row justify-end gap-2 p-2 bg-gray-50 rounded-lg">
        <Button className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-md" onClick={resetForm}>
          New
        </Button>
        <Button
          className="w-full sm:w-auto bg-green-500 text-white hover:bg-green-600 font-medium rounded-md"
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </div>
    </Modal>
  );
};

export default AddPayStructureForm;